import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
	Paper,
	Button,
	Popper,
	Grow,
	Typography,
	MenuItem,
	MenuList,
	ClickAwayListener,
} from '@mui/material';
import ArrowUpIcon from '@mui/icons-material/ArrowDropUp';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import classNames from 'classnames';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { Routes } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import { OrderProduct, Shopper } from 'common/types';
import { usePermissions } from 'hooks/usePermissions';
import { dateIsClosed, dateIsOutsideOpeningHours } from 'common/utils/dateCalculations';
import { useHasFeature } from 'hooks/useHasFeature';

interface Props {
	rentalProducts: OrderProduct[];
	shoppers: Shopper[];
	validateRentalAndGoToRoute: (route: string) => void;
	hasOverbooking: boolean;
}
const ConfirmBtns = (props: Props) => {
	const { rentalProducts, validateRentalAndGoToRoute, hasOverbooking } = props;
	const classes = useStyles();
	const rentNowToggled = useSelector(ViewSelectors.newRentalToggled);
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const startDate = useSelector(ViewSelectors.newRentalStartDate);
	const endDate = useSelector(ViewSelectors.newRentalEndDate);
	const from = moment(startDate);
	const to = moment(endDate);
	const isClosed = dateIsClosed(from, openingHours) || dateIsClosed(to, openingHours);
	const startTimeIsOutsideOpeningHours = dateIsOutsideOpeningHours(from, openingHours);
	const endTimeIsOutsideOpeningHours = dateIsOutsideOpeningHours(to, openingHours);
	const isOutsideOpeningHours =
		startTimeIsOutsideOpeningHours || endTimeIsOutsideOpeningHours || isClosed;
	const isMobile = window.matchMedia('screen and (max-width: 700px)').matches;
	const productsCount = rentalProducts.length;
	const { t } = useTranslation();
	const [continueMenuAnchor, setContinueMenuAnchor] = useState<null | HTMLElement>(null);
	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');
	const disabledButton =
		productsCount === 0 || (!allowOverbookings && (hasOverbooking || isOutsideOpeningHours));
	const { hasFeature } = useHasFeature();
	const showRentallinkButton = !hasFeature('HIDE_RENTAL_LINK_BUTTON', { scope: 'shop' });

	const handleContinueMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setContinueMenuAnchor(continueMenuAnchor ? null : event.currentTarget);
	};

	const handleContinueMenuClose = (event: any) => {
		if (continueMenuAnchor && continueMenuAnchor.contains(event.target)) {
			return;
		}
		setContinueMenuAnchor(null);
	};

	const renderRentNowContinueBtn = () => (
		<Button
			data-intercom-target="Continue button"
			variant="contained"
			color="primary"
			fullWidth={!isMobile}
			disabled={disabledButton}
			className={classNames(
				classes.fixedContinueBtn,
				classes.continueBtnMinHeight,
				!isMobile ? classes.continueBtnMargin : {},
			)}
			onClick={() => {
				validateRentalAndGoToRoute(Routes.NewRentalShopperInfo('rent-now'));
			}}
			data-qa="continue"
		>
			{t('common:actions.continue')}
		</Button>
	);
	const renderMobileReservationContinueBtns = () => (
		<>
			<Button
				variant="contained"
				color="primary"
				disabled={disabledButton}
				className={classes.fixedContinueBtn}
				onClick={handleContinueMenuClick}
			>
				<div className={classes.continueBtn}>
					{t('common:actions.continue')}
					<ArrowUpIcon className={classes.rightIcon} />
				</div>
			</Button>
			<Popper
				open={Boolean(continueMenuAnchor)}
				anchorEl={continueMenuAnchor}
				transition
				disablePortal
				placement="top-end"
			>
				{({ TransitionProps }) => (
					<Grow {...TransitionProps}>
						<Paper>
							<ClickAwayListener onClickAway={handleContinueMenuClose}>
								<MenuList>
									{showRentallinkButton && (
										<MenuItem
											onClick={() =>
												validateRentalAndGoToRoute(Routes.NewRentalShopperInfo('link'))
											}
										>
											<Typography variant="body2">
												{t('newRental.sendRentalLink2', 'Send link to order')}
											</Typography>
										</MenuItem>
									)}
									<MenuItem
										onClick={() => {
											validateRentalAndGoToRoute(Routes.NewRentalShopperInfo('reservation'));
										}}
									>
										<Typography variant="body2">{t('shopperInfo.saveRentalBtn')}</Typography>
									</MenuItem>
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);

	const renderDesktopReservationContinueBtns = () => (
		<>
			{showRentallinkButton && (
				<div>
					<Button
						data-intercom-target="Send link to rental button"
						variant="contained"
						color="primary"
						fullWidth
						disabled={disabledButton}
						className={classNames(
							classes.fixedContinueBtn,
							!isMobile ? classes.continueBtnMargin : {},
						)}
						onClick={() => {
							validateRentalAndGoToRoute(Routes.NewRentalShopperInfo('link'));
						}}
						data-qa="sendLink"
					>
						{t('newRental.sendRentalLink2', 'Send link to order')}
					</Button>
				</div>
			)}
			<div>
				<Button
					data-intercom-target="Save rental button"
					variant="outlined"
					color="primary"
					fullWidth
					disabled={disabledButton}
					className={classNames(
						classes.fixedContinueBtn,
						!isMobile ? classes.continueBtnMargin : {},
					)}
					onClick={() => {
						validateRentalAndGoToRoute(Routes.NewRentalShopperInfo('reservation'));
					}}
					data-qa="saveBooking"
				>
					{t('shopperInfo.saveBookingBtn2', 'Save order')}
				</Button>
			</div>
		</>
	);

	return rentNowToggled
		? renderRentNowContinueBtn()
		: !isMobile
		? renderDesktopReservationContinueBtns()
		: renderMobileReservationContinueBtns();
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		fixedContinueBtn: {
			flex: '0 0 auto',
		},
		continueBtnMinHeight: {
			minHeight: theme.spacing(6),
		},
		continueBtnMargin: {
			marginTop: theme.spacing(1),
		},
		rightIcon: {
			marginRight: -theme.spacing(1),
		},
		continueBtn: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
	}),
);

export default ConfirmBtns;
